import React from 'react'
import PropTypes from 'prop-types'
import { Menu } from '../../../dbWebUI'
import { Box, Text, Button, Icon, Input, CMSContent } from '@deseretbook/react-ui'

import './SearchSidebarContent.css'
import useContentSearch from '../../../hooks/useContentSearch'

/**
 * Displays a list of reader options. Mainly to be used in a `<Sidebar />` but could be
 * placed in other locations as well.
 * @todo add this to the storybook?
 */
const SearchSidebarContent = ({ onClose, isOpen, sku, ebook, onSelectChapter, ...rest }) => {
  const { query, setQuery, results, isLoading, hasSearched } = useContentSearch({
    skus: [sku],
  })

  const onChange = (e) => {
    setQuery(e.target.value)
  }

  return (
    <div id="SearchSidebarContent" {...rest}>
      <Box
        alignItems="center"
        justifyContent="space-between"
        padding={1}
        style={{ borderBottom: '1px solid #0000001A' }}
      >
        <Button onClick={onClose} className="sidebar-button-close">
          <Icon name="arrowRight" className="sidebar-button-icon" />
        </Button>
        <Text style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 700, fontSize: '20px' }}>
          {query && hasSearched && !isLoading && results.length
            ? `${results.length} Results`
            : 'Results'}
        </Text>
        <Box height="40px" width="40px" />
      </Box>
      <Box className="search-input-wrapper">
        <Input removeHint className="search-input" value={query} onChange={onChange} />
        <Button onClick={() => setQuery('')} className="search-button-clear">
          <Icon name="close" className="search-button-icon" />
        </Button>
      </Box>
      <Box style={{ margin: '20px' }} className="search-results">
        {query && hasSearched && !isLoading && (
          <>
            {results && results.length ? (
              <Menu hasVerticalScroll style={{ marginTop: '20px', padding: '0 20px' }}>
                <Menu.List>
                  {results.map((result) => {
                    const match = result.matches[0]
                    const { content } = match
                    const source = ebook.manifest.items.byDocumentId[match.source]
                    const pageIndex = ebook.manifest.items.pages.findIndex(
                      (page) => page.href === source.href,
                    )

                    const formattedContent = content
                      .replace(/^…\s*/, '') // Remove leading "…"
                      .replace(/\s*…$/, '') // Remove trailing "…"
                      .replace(
                        /<em>(.*?)<\/em>/g,
                        '<span class="search-result-highlight">$1</span>',
                      )
                    return (
                      <Menu.ListItem className="search-result-container">
                        <Menu.ListItem.Button
                          onClick={() => onSelectChapter({ ...match, src: source.href })}
                          className="search-result-button"
                        >
                          <Box>
                            <Text className="search-result-page">Page {pageIndex + 1}</Text>
                            <Text className="search-result-content">
                              <CMSContent
                                dangerouslySetInnerHTML={{ __html: formattedContent }}
                                itsNotDangerousBecause="Result content comes directly from the Bookshelf API, and should be trustworthy"
                              />
                            </Text>
                          </Box>
                        </Menu.ListItem.Button>
                      </Menu.ListItem>
                    )
                  })}
                </Menu.List>
              </Menu>
            ) : (
              <Box className="no-results">
                <Icon name="noResults" className="no-results-icon" />
                <Text className="no-results-text">Sorry, we couldn't find any results</Text>
              </Box>
            )}
          </>
        )}
      </Box>
    </div>
  )
}

SearchSidebarContent.defaultProps = {
  tableOfContents: [],
  onSelectChapter: () => {},
  isVisible: true,
  className: '',
}

SearchSidebarContent.propTypes = {
  /** Table of contents items */
  tableOfContents: PropTypes.arrayOf(PropTypes.object),
  /** callback for selecting a chapter */
  onSelectChapter: PropTypes.func,
  /** if the table of contents is currently being displayed. */
  isVisible: PropTypes.bool,
  /** Additional class names */
  className: PropTypes.string,
}

export default SearchSidebarContent
